'use client';
import { useCurrentPlayingAudioUrl } from '@mnd-frontend/contexts';
import IconChevronLeft from '@mnd-frontend/icons/components/ChevronLeft';
import IconChevronRight from '@mnd-frontend/icons/components/ChevronRight';
import { Spinner, Text } from '@mnd-frontend/ui/publish/components';
import { useToasts } from '@mnd-frontend/ui/publish/contexts';
import {
  GREY_300,
  GREY_400,
  GREY_500,
  GREY_700,
  GREY_800,
  GREY_1000,
  NEUTRAL_WHITE,
  RED_600,
  RED_700,
} from '@mnd-frontend/ui/shared/tokens';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { ReactComponent as PauseIcon } from './assets/icon_pause.svg?react';
import { ReactComponent as PlayIcon } from './assets/icon_play.svg?react';

const formatTimestamp = (timestamp: number): string => {
  const hours = Math.floor(timestamp / 3600);
  const minutes = Math.floor((timestamp % 3600) / 60);
  const seconds = Math.floor(timestamp % 60);
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(seconds).padStart(2, '0');

  return hours > 0
    ? `${hours}:${formattedMinutes}:${formattedSeconds}`
    : `${formattedMinutes}:${formattedSeconds}`;
};

const TimeStamp = styled.span`
  font-family: 'Roboto Mono', monospace;
`;

const ControlWrapper = styled.div`
  display: flex;
  gap: 2px;
`;

const buttonStyles = css`
  font-family: 'Roboto Mono', monospace;
  transition:
    background-color 0.1s ease-out,
    color 0.1s ease-out;
  border: none;
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  border-radius: 1rem;
  line-height: 1;
  word-wrap: nowrap;
  background-color: ${GREY_300};
  color: ${GREY_1000};
  overflow: hidden;

  [disabled] {
    color: ${GREY_700};
    pointer-events: none;
  }
`;

const NextPrevButton = styled.button`
  height: 100%;
  width: 1.5rem;
  border: none;
  cursor: pointer;
  padding: 0;
  font-weight: 700;

  &:hover {
    background-color: ${GREY_400};
  }
  &:focus {
    background-color: ${GREY_500};
    color: ${GREY_1000};
  }
`;

export const A = styled.a`
  &,
  &:active,
  &:focus,
  &:visited,
  &:hover {
    color: inherit;
    text-decoration: none;
  }
  &:hover {
    text-decoration: underline;
  }
`;

const NextPrevControl = styled.div`
  ${buttonStyles}
  gap: 0.25rem;
  padding: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
`;

const PlayStateButton = styled.button<{ $active?: boolean }>`
  ${buttonStyles}
  cursor: pointer;

  ${NextPrevControl} + & {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  svg,
  svg path {
    fill: ${GREY_1000}; /* To override style in spinner */
  }
  &:hover {
    background-color: ${GREY_400};
  }
  &:focus {
    background-color: ${GREY_500};
    color: ${GREY_1000};
    svg path {
      /* To override style in spinner */
      fill: ${GREY_800};
    }
  }

  ${props =>
    props.$active &&
    css`
      background-color: ${RED_700};
      color: ${NEUTRAL_WHITE};
      svg,
      svg path {
        fill: ${NEUTRAL_WHITE}; /* To override style in spinner */
      }
      &:hover {
        background-color: ${RED_600};
      }
      &:focus {
        background-color: ${RED_700};
      }
    `}
`;
const Hitline = styled.span`
  font-style: italic;
  word-break: break-word;
  &:before {
    content: 'â';
  }
  &:after {
    content: 'â';
  }
`;
const Player = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const AudioPlayer = ({
  audioUrl,
  articleLink,
  hitlines,
  hideHitlineText,
}: {
  audioUrl: string;
  articleLink?: string | null;
  hideHitlineText?: boolean;
  hitlines: {
    hitline: string;
    startPos: number;
    endPos: number;
  }[];
}) => {
  const { t } = useTranslation();
  const { showFailureToast } = useToasts();
  const [hitlineIndex, setHitlineIndex] = useState(0);
  const { startPos, endPos } = hitlines[hitlineIndex];
  const hasMultipleHitlines = hitlines.length > 1;
  const audioRef = useRef<HTMLAudioElement>(null);
  const audio = audioRef.current;
  const [currentTime, setCurrentTime] = useState(startPos);
  const [playState, setPlayState] = useState<
    'pristine' | 'playing' | 'paused' | 'stopped' | 'loading'
  >('pristine');
  const { currentlyPlayingAudioUrl, setCurrentlyPlayingAudioUrl } = useCurrentPlayingAudioUrl();

  const handlePlay = async (startFrom: number) => {
    const audio = audioRef.current;
    let timeout;
    if (audio) {
      try {
        timeout = setTimeout(() => setPlayState('loading'), 150); // Slight delay to avoid flickering
        audioRef.current.currentTime = startFrom;
        await audio.play();
        clearTimeout(timeout);
        setPlayState('playing');
        setCurrentlyPlayingAudioUrl(audioUrl);
      } catch (_error) {
        clearTimeout(timeout);
        showFailureToast();
        setPlayState('paused');
      }
    }
  };

  const handleUnpause = () => {
    if (audio) {
      audio.play();
      setPlayState('playing');
    }
  };

  const handlePause = () => {
    if (audio) {
      audio.pause();
      setPlayState('paused');
    }
  };

  const handleTimeUpdate = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      setCurrentTime(Math.max(startPos, Math.min(endPos, audio.currentTime)));
    }
    if (audio && audio.currentTime > endPos) {
      audio.pause();
      setPlayState('stopped');
      setCurrentTime(startPos);
    }
  }, [startPos, endPos]);

  const handleCanPlay = useCallback(() => {
    if (playState === 'loading') {
      setPlayState('playing');
    }
  }, [playState]);

  const playIndex = (index: number) => {
    const newTime = hitlines[index].startPos;
    setCurrentTime(newTime);
    setHitlineIndex(index);
    setPlayState('stopped');
    if (playState === 'playing') {
      handlePlay(newTime);
    }
  };

  useEffect(() => {
    if (currentlyPlayingAudioUrl !== audioUrl) {
      setPlayState('stopped');
      audioRef.current?.pause();
    }
  }, [currentlyPlayingAudioUrl, audioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('canplay', handleCanPlay);
    }

    return () => {
      if (audio) {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('canplay', handleCanPlay);
      }
    };
  }, [handleTimeUpdate, handleCanPlay]);

  return (
    <Player role="region" aria-label={t('common_media_control_audio_player')}>
      <audio ref={audioRef} src={audioUrl} preload="none">
        <track kind="captions" />
      </audio>
      <ControlWrapper>
        {hasMultipleHitlines && (
          <NextPrevControl>
            <NextPrevButton
              type="button"
              onClick={() => playIndex(hitlineIndex - 1)}
              disabled={hitlineIndex === 0}
              aria-label={t('common_previous')}
            >
              <IconChevronLeft $size="xxs" />
            </NextPrevButton>
            {hitlineIndex + 1}/{hitlines.length}
            <NextPrevButton
              type="button"
              onClick={() => playIndex(hitlineIndex + 1)}
              disabled={hitlineIndex === hitlines.length - 1}
              aria-label={t('common_next')}
            >
              <IconChevronRight $size="xxs" />
            </NextPrevButton>
          </NextPrevControl>
        )}
        {(playState === 'pristine' || playState === 'stopped' || playState === 'paused') && (
          <PlayStateButton
            type="button"
            onClick={playState === 'paused' ? handleUnpause : () => handlePlay(startPos)}
            aria-label={t('common_media_control_play')}
          >
            <PlayIcon />
            <TimeStamp>
              {formatTimestamp(currentTime)} - {formatTimestamp(endPos)}
            </TimeStamp>
          </PlayStateButton>
        )}
        {playState === 'loading' && (
          <PlayStateButton
            type="button"
            $active={true}
            aria-disabled="true"
            aria-busy="true"
            aria-label={t('common_media_control_loading')}
          >
            <Spinner size="xs" />
            <TimeStamp>
              {formatTimestamp(currentTime)} - {formatTimestamp(endPos)}
            </TimeStamp>
          </PlayStateButton>
        )}
        {playState === 'playing' && (
          <PlayStateButton
            type="button"
            $active={true}
            onClick={handlePause}
            aria-label={t('common_media_control_pause')}
          >
            <PauseIcon />
            <TimeStamp>
              {formatTimestamp(currentTime)} - {formatTimestamp(endPos)}
            </TimeStamp>
          </PlayStateButton>
        )}
      </ControlWrapper>
      {!hideHitlineText && (
        <Text as="p" variant="body">
          {articleLink ? (
            <A href={articleLink} target="_blank" rel="noopener noreferrer">
              <Hitline dangerouslySetInnerHTML={{ __html: hitlines[hitlineIndex].hitline || '' }} />
            </A>
          ) : (
            <Hitline dangerouslySetInnerHTML={{ __html: hitlines[hitlineIndex].hitline || '' }} />
          )}
        </Text>
      )}
    </Player>
  );
};
